import React from "react"

import { PracticePayerMappingsBulkMode } from "@common-shared/config/practice.config"

import type { IPopupProps } from "@front-shared/types/props.types"

import { PAYER_MAPPINGS_CONFIG } from "../../config/payerMappings.config"
import { PayerMappingsCsvHelper } from "../../helpers/payerMappingsCsv.helper"
import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import { payerMappingsValidateBulk } from "../../store/thunks/payerMappings.thunks"
import type { IReviewPayerMappingsState } from "../../types/payerMappings.types"
import { PayerMappingsInfoNoticeComponent } from "../configurations/payerMappings/payerMappingsInfoNotice.component"
import { BulkUploadPopupComponent } from "./bulkUploadPopup.component"

export const PayerMappingsBulkUploadPopupComponent: React.FunctionComponent<
    IPayerMappingsBulkUploadPopupComponentProps
> = props => {
    const dispatch = useAppDispatch()
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    return (
        <BulkUploadPopupComponent
            isActive={props.isActive}
            className={props.className}
            closeOnOutsideClick={props.closeOnOutsideClick}
            onDone={props.onDone}
            title="Add bulk payer mappings"
            description="Import multiple payer mappings at once with a CSV."
            dropzoneLabel="Add Payer Mappings CSV"
            importButtonLabel="Import mappings"
            parsingLabel="Parsing your payer mappings..."
            templateUrl={PAYER_MAPPINGS_CONFIG.csvTemplateUrl}
            notice={
                <PayerMappingsInfoNoticeComponent subject="Payer mapping" />
            }
            disabled={!selectedPracticeRole}
            parseCsv={PayerMappingsCsvHelper.parse}
            onValidate={(items, file, callbacks) => {
                if (!selectedPracticeRole) {
                    callbacks.onError()

                    return
                }

                dispatch(
                    payerMappingsValidateBulk({
                        practice: selectedPracticeRole.practice,
                        payload: {
                            mode: PracticePayerMappingsBulkMode.REPLACE,
                            items: PayerMappingsCsvHelper.toRequestItems(items),
                        },
                        onSuccess: result => {
                            props.onValidationDone({
                                items,
                                errors: PayerMappingsCsvHelper.mapBulkErrors(
                                    result?.errors ?? [],
                                ),
                                filename: file.name,
                                totalRowCount: items.length,
                                deletedCount: result?.deleted ?? 0,
                                createdCount: result?.created ?? 0,
                                updatedCount: result?.updated ?? 0,
                            })

                            callbacks.onSuccess()
                        },
                        onError: callbacks.onError,
                    }),
                )
            }}
        />
    )
}

interface IPayerMappingsBulkUploadPopupComponentProps extends IPopupProps {
    onValidationDone: (data: IReviewPayerMappingsState) => void
}
